import { toast } from 'sonner';
import { createElement } from 'react';
import { Sparkles, Check, Minus, AlertCircle, Bell } from 'lucide-react';

/**
 * Exora signature notification system.
 * Refined Lucide glyphs in brand gold — luxury, unified, not ASCII.
 *
 * Mapping:
 *   neutral  → Sparkles    (informational)
 *   success  → Check       (confirmed action)
 *   remove   → Minus       (removal/clearing)
 *   error    → AlertCircle (problem)
 *   push     → Bell        (live notification)
 *
 * Each variant accepts either a plain string or { title, description }
 * for richer concierge-style messaging.
 */

type ToastInput = string | { title: string; description?: string };

const iconBase = 'h-4 w-4 shrink-0';
const goldClass = `${iconBase} text-[hsl(var(--gold))]`;
const mutedClass = `${iconBase} text-white/50`;
const errorClass = `${iconBase} text-[hsl(0_72%_60%)]`;

const normalize = (input: ToastInput): { title: string; description?: string } =>
  typeof input === 'string' ? { title: input } : input;

export const exoraToast = {
  /** Neutral / informational */
  neutral: (input: ToastInput) => {
    const { title, description } = normalize(input);
    toast(title, {
      icon: createElement(Sparkles, { className: goldClass, strokeWidth: 1.75 }),
      description,
      duration: 4000,
    });
  },

  /** Confirmed / success actions */
  success: (input: ToastInput) => {
    const { title, description } = normalize(input);
    toast(title, {
      icon: createElement(Check, { className: goldClass, strokeWidth: 2.25 }),
      description,
      duration: 3200,
    });
  },

  /** Removal / clearing */
  remove: (input: ToastInput) => {
    const { title, description } = normalize(input);
    toast(title, {
      icon: createElement(Minus, { className: mutedClass, strokeWidth: 2 }),
      description,
      duration: 3000,
    });
  },

  /** Errors */
  error: (input: ToastInput) => {
    const { title, description } = normalize(input);
    toast(title, {
      icon: createElement(AlertCircle, { className: errorClass, strokeWidth: 1.75 }),
      description,
      duration: 5200,
    });
  },

  /** Push notification foreground toast */
  push: ({ title, body, duration = 6000, onClick }: {
    title: string;
    body?: string;
    duration?: number;
    onClick?: () => void;
  }) => {
    toast(title, {
      icon: createElement(Bell, { className: goldClass, strokeWidth: 1.75 }),
      description: body,
      duration,
      ...(onClick ? { action: { label: '→', onClick } } : {}),
    });
  },
};
